import React from 'react';
import { observer } from 'src/utils/mobx-react';
import { I18n } from 'src/domains/layouts/webview/components/language/I18n';
import { RealexTabState } from 'src/domains/players/webview/components/WithdrawAndDeposit/depositProcedure/realexProvider/RealexTab.state';
import { KycWarning } from 'src/domains/players/webview/components/WithdrawAndDeposit/depositProcedure/topUpProcedureParts/kycWarning/KycWarning';
import { PaymentAdditions } from 'src/domains/players/webview/components/WithdrawAndDeposit/depositProcedure/depositSignupPage/PaymentsAdditions';
import { RingFencedFundsMessage } from 'src/domains/players/webview/components/WithdrawAndDeposit/depositProcedure/topUpProcedureParts/ringFencedFundsWrapper/RingFencedFundsWrapper';
import { PayButton } from 'src/domains/players/webview/components/WithdrawAndDeposit/depositProcedure/realexProvider/RealexTab.style';
import {
    CardInfo,
    CashBalance,
    CashBalanceWrapper,
    DepositTabWrapper,
} from 'src/domains/players/webview/components/WithdrawAndDeposit/depositProcedure/DepositProcedure.style';
import { Input } from 'src/domains/players/webview/components/form/input/Input';
import { LanguagesState } from 'src/domains/layouts/state/languagesState/LanguagesState';
import { BasicDataModel } from 'src/domains/players/state/BasicDataModel';
import { RemainingLimit } from 'src/domains/players/webview/components/WithdrawAndDeposit/depositProcedure/topUpProcedureParts/remainingLimit/RemainingLimit';
import { useCommon } from 'src/domains/common/Common';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { YaspaSegment } from 'src/domains/players/webview/components/WithdrawAndDeposit/yaspa/YaspaSegment/YaspaSegment';
import { EnvironmentState } from 'src/domains/layouts/state/environmentState/EnvironmentState';
import { isMobileVersionEqualOrHigher } from 'src/utils/mobileApp';
import { RingFencedFundsMessageUpdated } from 'src/domains/players/webview/components/WithdrawAndDeposit/depositProcedure/topUpProcedureParts/ringFencedFundsWrapper/RingFencedFundsWrapperUpdated';
import { mapErrorsMessages } from 'src/domains/players/webview/components/WithdrawAndDeposit/depositProcedure/topUpProcedureParts/errorMessages/DepositErrorMessages';

interface RealexTabType {
    state: RealexTabState;
}

export const RealexTab = observer('RealexTab', ({ state }: RealexTabType): JSX.Element => {
    const common = useCommon();

    const {
        config: { showKycStatus, yaspaMobileAppValidVersion, isSaferGambling, paymentProviders },
    } = ConfigComponents.get(common);
    const languagesState = LanguagesState.get(common);
    const basicDataModel = BasicDataModel.get(common);
    const envState = EnvironmentState.get(common);
    const isMobileApp = envState.isMobileApp();
    const mobileAppVersion = envState.mobileAppVersion;
    const shouldDisplayYaspaForMobileApp = isMobileApp
        ? isMobileVersionEqualOrHigher(mobileAppVersion, yaspaMobileAppValidVersion)
        : true;

    return (
        <DepositTabWrapper>
            <CashBalanceWrapper data-test='cash-balance'>
                <I18n
                    langKey='account.top-up.cash-balance-label'
                    defaultText='Cash balance'
                />
                <CashBalance>{state.showBalance}</CashBalance>
            </CashBalanceWrapper>

            <Input
                type='text'
                maxLength={10}
                placeholder='0.00'
                currency={basicDataModel.moneySymbol}
                label={
                    <I18n
                        langKey='account.top-up.description-label'
                        defaultText='Deposit amount'
                    />
                }
                state={state.depositAmount}
                onChange={state.onChange}
                onBlur={state.setAmount}
                colorTheme='light'
                inputHasBackground={false}
                dataTest='deposit-amount-input'
            />

            <PaymentAdditions
                key='payment-additions'
                currency={basicDataModel.moneySymbol}
                value={state.depositAmount.value}
                onChange={state.handleAdditionsChange}
            />

            {state.remainingLimitState.remainingLimit === undefined ? null : (
                <RemainingLimit
                    isSignup={state.isSignup}
                    languagesState={languagesState}
                    value={state.remainingLimitState.remainingLimit.format(basicDataModel.currency)}
                />
            )}

            {state.errorMessagesState.errors.length > 0
                ? state.errorMessagesState.errors.map((error) => mapErrorsMessages(error, languagesState))
                : null}

            {state.hasRingFencedFunds === false ? (
                isSaferGambling.available ? (
                    <RingFencedFundsMessageUpdated isSignup={state.isSignup} />
                ) : (
                    <RingFencedFundsMessage
                        isMoreShown={state.showMoreRingFenceFlag}
                        toggleShowMore={state.toggleShowMore}
                        isSignup={state.isSignup}
                    />
                )
            ) : null}
            <PayButton
                size='large'
                disabled={state.isButtonDisabled || state.areMissingFieldsForBillingInfoRealex()}
                onClick={state.submitDepositForm}
                type='submit'
                dataTest='deposit-pay-debit-card'
            >
                <I18n
                    langKey='account.top-up.pay-card-button'
                    defaultText='Pay using debit card'
                />
            </PayButton>

            <CardInfo data-test='deposit-card-info'>
                <I18n
                    langKey='account.top-up.top-upcard-info'
                    defaultText='Credit cards are no longer accepted'
                />
            </CardInfo>
            {paymentProviders.openBanking?.type === 'yaspa' &&
            paymentProviders.openBanking.allowOnlyWithdraw === false &&
            shouldDisplayYaspaForMobileApp ? (
                <YaspaSegment
                    viewType='deposit'
                    depositYaspaPage={state.depositYaspaPage}
                    isSignup={state.isSignup}
                    errorMessagesState={state.errorMessagesState}
                />
            ) : null}

            {showKycStatus === true ? <KycWarning isSignup={false} /> : null}
        </DepositTabWrapper>
    );
});
